import React, { memo, useCallback } from 'react';
import COLORS from 'constants/colors';
import { DETECTION_TYPES_CONFIG } from 'constants/av-review';
import { isInvalidReasonRequired } from '../../helpers';

import { Text, Dropdown, Separator } from '@cuemath/web-comps';
import { FlexView } from '@cuemath/comps';

import {
  VerdictContainer,
  ValidButton,
  InvalidButton,
  ButtonsWrapper,
  ReasonBlock,
  ReasonDropdownWrapper,
} from './verdict-buttons-styled';
import { TextButton } from '../../av-review-detail-styled';

const VerdictButtons = ({
  event,
  isThresholdMet,
  isVerdictBusy,
  onSubmitVerdict,
  isReReview,
  onBackToSummary,
  detectionType,
}) => {
  const invalidReasons = DETECTION_TYPES_CONFIG[detectionType]?.invalidReasons || [];
  const reasonRequired = isInvalidReasonRequired(detectionType);
  const currentInvalidReason = event?.invalid_reason || null;
  const handleValid = useCallback(() => onSubmitVerdict(true), [onSubmitVerdict]);
  const handleInvalid = useCallback(
    () => onSubmitVerdict(false, currentInvalidReason),
    [onSubmitVerdict, currentInvalidReason],
  );
  const handleReasonChange = useCallback(
    value => onSubmitVerdict(false, value),
    [onSubmitVerdict],
  );

  if (!event) return null;

  const currentVerdict = event.is_valid;
  const chunks = event.event_data || [];
  const totalChunks = chunks.length;
  const viewedCount = chunks.reduce((n, c) => (c.is_viewed ? n + 1 : n), 0);
  const isDisabled = !isThresholdMet || isVerdictBusy;
  const showReasonDropdown = currentVerdict === false && reasonRequired;

  return (
    <VerdictContainer>
      <FlexView flexDirection="row" alignItems="center" justifyContent="space-between">
        <FlexView>
          {isReReview && (
            <TextButton onClick={onBackToSummary}>
              <Text variant="cta2" color={COLORS.BLACK}>
                {'\u2190'} Back to summary
              </Text>
            </TextButton>
          )}
        </FlexView>
        <FlexView flexDirection="row" justifyContent="flex-end" alignItems="center">
          <Text variant="body3" color={COLORS.BLACK_T_60}>
            {isThresholdMet
              ? `All viewing requirements met (${viewedCount}/${totalChunks} chunks viewed)`
              : `View more chunks before submitting verdict (${viewedCount}/${totalChunks} viewed)`}
          </Text>
          <ButtonsWrapper flexDirection="row" alignItems="center">
            <ValidButton
              disabled={isDisabled}
              isSelected={currentVerdict === true}
              onClick={handleValid}
            >
              <Text
                variant="cta2"
                color={currentVerdict === true ? COLORS.WHITE : COLORS.GREEN_6}
              >
                {'\u2713'} Event valid
              </Text>
            </ValidButton>
            <InvalidButton
              disabled={isDisabled}
              isSelected={currentVerdict === false}
              onClick={handleInvalid}
            >
              <Text
                variant="cta2"
                color={currentVerdict === false ? COLORS.WHITE : COLORS.RED_6}
              >
                {'\u2717'} Event invalid
              </Text>
            </InvalidButton>
          </ButtonsWrapper>
        </FlexView>
      </FlexView>
      {showReasonDropdown && (
        <>
          <Separator height={16} transparent />
          <ReasonBlock flexDirection="row" alignItems="center" justifyContent="flex-end">
            <Text variant="body3" color={COLORS.BLACK_T_60}>
              Not valid? Please select a reason{' '}
              <Text display="inline" variant="body3" color={COLORS.RED_4}>
                *
              </Text>
            </Text>
            <ReasonDropdownWrapper>
              <Dropdown
                type="primary"
                width="100%"
                optionsWidth="220px"
                height="auto"
                name="invalid-reason"
                placeholder="Select a reason"
                value={event.invalid_reason || ''}
                options={invalidReasons}
                onChange={handleReasonChange}
                disabled={isVerdictBusy}
                restrictSearch
                autoHideOnSelect
              />
            </ReasonDropdownWrapper>
          </ReasonBlock>
        </>
      )}
    </VerdictContainer>
  );
};

export default memo(VerdictButtons);
